import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { scheduleKeys } from './queryKeys';
import { mapShiftRpcError } from '@/hooks/useSchedulingMutations';

/**
 * Dedicated hook for offering a shift (making it available for others to claim).
 * 
 * Uses correct per-query snapshot/rollback via getQueriesData to avoid
 * the prefix-key shape mismatch bug that existed in the original implementation.
 */
export const useOfferShift = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (shiftId: string) => {
      const { error } = await supabase.rpc('offer_shift', { p_shift_id: shiftId });
      if (error) throw error;
    },

    onMutate: async (shiftId) => {
      // Cancel all in-flight schedule queries to avoid overwrites
      await queryClient.cancelQueries({ queryKey: scheduleKeys.all });

      // Snapshot ALL schedule queries for safe rollback (returns [key, data][])
      const previousQueries = queryClient.getQueriesData<unknown>({ queryKey: scheduleKeys.all });

      // Apply optimistic update ONLY to shift-array query families
      const shiftPrefixes = [
        [...scheduleKeys.all, 'myShifts'],
        [...scheduleKeys.all, 'companyShifts'],
        [...scheduleKeys.all, 'openShifts'],
      ];

      for (const prefix of shiftPrefixes) {
        queryClient.setQueriesData<any[]>({ queryKey: prefix }, (old) => {
          if (!Array.isArray(old)) return old;
          return old.map((shift) =>
            shift.id === shiftId
              ? { ...shift, profile_id: null, status: 'offered' }
              : shift
          );
        });
      }

      return { previousQueries };
    },

    onError: (error: any, _shiftId, context) => {
      // Restore every query individually from the snapshot
      if (context?.previousQueries) {
        for (const [key, data] of context.previousQueries) {
          queryClient.setQueryData(key, data);
        }
      }
      const errorMessage = mapShiftRpcError(error?.message);
      toast.error(errorMessage);
    },

    onSuccess: () => {
      toast.success('Shift has been successfully offered.');
    },

    onSettled: () => {
      // Always refetch to ensure consistency
      queryClient.invalidateQueries({ queryKey: scheduleKeys.all });
    },
  });
};
